/* ==========================================================================
   Wild Touch Engraving - motion core

   One registry, one observer, one rAF loop. Sections declare motion in
   Liquid with data-anim="<name>"; nothing is hard-coded per section.

   Deliberately dependency-free. GSAP + ScrollTrigger + SplitText would add
   ~70KB gz for the six behaviours below, all of which IntersectionObserver,
   the Web Animations API and CSS transitions already cover. If a future
   section needs real timeline scrubbing or Flip, add GSAP then.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var tier = root.getAttribute('data-motion-tier') || 'standard';
  var isMinimal = tier === 'minimal';
  var isFull = tier === 'full';

  /* ---------------------------------------------------------------- utils */

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function ms(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v * 1000;
  }

  /* Single shared rAF loop. Everything that needs a frame subscribes here. */
  var frameJobs = [];
  var running = false;

  function tick() {
    running = false;
    for (var i = frameJobs.length - 1; i >= 0; i--) {
      if (frameJobs[i]() === false) frameJobs.splice(i, 1);
    }
    if (frameJobs.length) schedule();
  }

  function schedule() {
    if (running || document.hidden) return;
    running = true;
    requestAnimationFrame(tick);
  }

  function onFrame(job) {
    frameJobs.push(job);
    schedule();
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) schedule();
  });

  /* ------------------------------------------------------------- registry */

  var registry = {};

  function define(name, setup) {
    registry[name] = setup;
  }

  /* engrave-text -------------------------------------------------------- */
  /* Split into lines, then chars. A gold point travels each baseline; every
     char resolves from blurred + dropped as the point passes it. */

  define('engrave-text', function (el) {
    var perChar = ms(el, 'data-anim-stagger', 0.024);
    var base = ms(el, 'data-anim-delay', 0);
    var lineGap = ms(el, 'data-anim-line-stagger', 0.12);

    if (!el.dataset.split) {
      // Only split leaf text. Anything with markup inside is left intact.
      var isWrapped = el.children.length > 0;
      var sources = isWrapped ? Array.prototype.slice.call(el.children) : [el];
      var index = 0;

      sources.forEach(function (source, li) {
        if (source.querySelector && source.querySelector('*')) return;
        var frag = document.createDocumentFragment();

        // Split to words first, then chars inside them: a word must never
        // break mid-way just because its letters are separate inline-blocks.
        source.textContent.split(/(\s+)/).forEach(function (token) {
          if (/^\s+$/.test(token)) {
            frag.appendChild(document.createTextNode(' '));
            return;
          }
          var word = document.createElement('span');
          word.className = 'wt-word';
          token.split('').forEach(function (ch) {
            var span = document.createElement('span');
            span.className = 'wt-char';
            span.textContent = ch;
            span.style.setProperty('--char-delay', base + li * lineGap + index * perChar + 'ms');
            index++;
            word.appendChild(span);
          });
          frag.appendChild(word);
        });

        source.textContent = '';
        source.appendChild(frag);
        source.classList.add('wt-line');
        source.style.setProperty('--line-delay', base + li * lineGap + 'ms');
      });

      if (!isWrapped) {
        el.classList.add('wt-line');
        el.style.setProperty('--line-delay', base + 'ms');
      }
      el.dataset.split = 'true';
    }

    return function () {
      el.classList.add('is-cut');
    };
  });

  /* chisel-in / reveal --------------------------------------------------- */

  define('chisel-in', function (el) {
    el.style.setProperty('--anim-delay', ms(el, 'data-anim-delay', 0) + 'ms');
    return function () {
      el.classList.add('is-cut');
    };
  });
  registry.reveal = registry['chisel-in'];

  /* scribe-frame --------------------------------------------------------- */
  /* Measures each stroke so the dash animation runs at a true 1:1 length. */

  define('scribe-frame', function (el) {
    var strokes = el.querySelectorAll('path, rect, polygon, line, circle');
    el.style.setProperty('--anim-delay', ms(el, 'data-anim-delay', 0) + 'ms');

    /* These frames use vector-effect: non-scaling-stroke, which puts the dash
       array in SCREEN pixels, not user units. getTotalLength() reports user
       units, so it tiles the stroke into dashes on any stretched viewBox.
       Measure the rendered box instead, at reveal time when layout is settled. */
    function measure() {
      var box = el.getBoundingClientRect();
      var w = box.width || 1;
      var h = box.height || 1;

      each(strokes, function (s, i) {
        var tag = s.tagName.toLowerCase();
        var len;
        if (tag === 'line') len = Math.hypot(w, h);
        else if (tag === 'polygon') len = 2 * Math.hypot(w, h);
        else len = 2 * (w + h);
        s.style.setProperty('--len', Math.ceil(len));
        s.style.transitionDelay = i * 90 + 'ms';
      });
    }

    measure();

    return function () {
      measure();
      /* let the new dash array land before the offset transitions to 0 */
      requestAnimationFrame(function () {
        el.classList.add('is-cut');
      });
    };
  });

  /* count-engrave -------------------------------------------------------- */

  define('count-engrave', function (el) {
    var target = parseFloat(el.getAttribute('data-count-to'));
    if (isNaN(target)) return null;

    var suffix = el.getAttribute('data-count-suffix') || '';
    var prefix = el.getAttribute('data-count-prefix') || '';
    var decimals = parseInt(el.getAttribute('data-count-decimals') || '0', 10);
    var duration = ms(el, 'data-anim-duration', 1.4);

    el.classList.add('count');
    el.textContent = prefix + (0).toFixed(decimals) + suffix;

    return function () {
      el.classList.add('is-cut');
      if (isMinimal) {
        el.textContent = prefix + target.toFixed(decimals) + suffix;
        return;
      }
      var start = performance.now();
      onFrame(function () {
        var p = Math.min((performance.now() - start) / duration, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
        return p < 1;
      });
    };
  });

  /* depth-parallax ------------------------------------------------------- */
  /* Rate 0.9 / 0.94 / 1.0, capped at 60px total travel per spec 5.4. */

  var parallaxItems = [];

  define('depth-parallax', function (el) {
    if (isMinimal) return null;
    var rate = parseFloat(el.getAttribute('data-parallax-rate') || '0.92');
    parallaxItems.push({ el: el, rate: rate, active: false });
    return function () {
      /* entrance is the parallax engaging; there is no separate reveal */
      el.classList.add('is-cut');
    };
  });

  /* pan-x ---------------------------------------------------------------- */
  /* Vertical scroll drives a horizontal track. The wrapper is made as tall as
     the horizontal travel, and the viewport inside it is pinned by CSS
     `position: sticky`, so there is no scroll hijacking and no jump: the
     browser still owns the scroll. */

  var panItems = [];

  define('pan-x', function (el) {
    var track = el.querySelector('.pan__track');
    if (!track) return null;

    var item = { el: el, track: track, travel: 0 };

    function measure() {
      /* below 900px CSS turns this into a normal swipe rail */
      if (window.innerWidth < 900 || isMinimal) {
        el.style.height = '';
        item.travel = 0;
        el.style.setProperty('--pan', 0);
        return;
      }
      /* Pin duration = wrapper height minus the sticky viewport's own
         height, so it must match the viewport's real rendered height, not
         a blind window.innerHeight guess (that reserved a full screen of
         scroll even when the tiles were far shorter than the viewport). */
      var viewport = el.querySelector('.pan__viewport');
      var viewportHeight = viewport ? viewport.getBoundingClientRect().height : window.innerHeight;
      item.travel = Math.max(0, track.scrollWidth - window.innerWidth + 48);
      el.style.height = viewportHeight + item.travel + 'px';
    }

    item.measure = measure;
    measure();
    panItems.push(item);

    return function () {
      el.classList.add('is-cut');
    };
  });

  /* sticky-stack --------------------------------------------------------- */
  /* Each panel pins, and shrinks back as the next one slides over it. */

  var stackPanels = [];

  define('sticky-stack', function (el) {
    if (isMinimal) return null;
    each(el.querySelectorAll('.stack-panel'), function (panel) {
      stackPanels.push(panel);
    });
    return function () {
      el.classList.add('is-cut');
    };
  });

  /* magnetic ------------------------------------------------------------- */

  function bindMagnetic(el) {
    if (!isFull) return;
    var radius = 120;
    var pull = 8;
    var rect = null;

    function refresh() {
      rect = el.getBoundingClientRect();
    }

    el.addEventListener('pointerenter', refresh);
    el.addEventListener(
      'pointermove',
      function (e) {
        if (!rect) refresh();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.hypot(dx, dy);
        var strength = Math.max(0, 1 - dist / (radius + rect.width / 2));
        el.style.setProperty('--mx', (dx / (rect.width / 2)) * pull * strength + 'px');
        el.style.setProperty('--my', (dy / (rect.height / 2)) * pull * strength + 'px');
      },
      { passive: true }
    );

    el.addEventListener('pointerleave', function () {
      el.style.setProperty('--mx', '0px');
      el.style.setProperty('--my', '0px');
    });
  }

  /* ---------------------------------------------------- the one observer */

  var revealed = new WeakSet();

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var el = entry.target;
              observer.unobserve(el);
              if (revealed.has(el)) return;
              revealed.add(el);
              var play = el.__wtPlay;
              el.style.willChange = 'transform, opacity';
              if (play) play();
              setTimeout(function () {
                el.style.willChange = '';
              }, 1600);
            });
          },
          /* threshold 0, not a ratio: resting elements are clipped to zero
             area by chisel-in, and a ratio threshold can never be met by a
             zero-area box. The rootMargin does the "wait until it is properly
             on screen" job instead. */
          { threshold: 0, rootMargin: '0px 0px -10% 0px' }
        )
      : null;

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-anim]');

    each(nodes, function (el) {
      if (el.__wtBound) return;
      el.__wtBound = true;

      var name = el.getAttribute('data-anim');
      var setup = registry[name];
      if (!setup) return;

      var play = setup(el);
      if (!play) return;

      /* Auto-stagger siblings that did not declare their own delay. */
      if (!el.hasAttribute('data-anim-delay') && el.parentElement) {
        var group = el.parentElement.getAttribute('data-anim-group');
        if (group) {
          var siblings = el.parentElement.querySelectorAll('[data-anim]');
          var i = Array.prototype.indexOf.call(siblings, el);
          var step = parseFloat(group) || 90;
          el.style.setProperty('--anim-delay', Math.min(i * step, 540) + 'ms');
        }
      }

      el.__wtPlay = play;

      if (!observer) {
        play();
        return;
      }

      /* Anything already on screen reveals now rather than waiting for the
         first observer callback. Content must never sit invisible because a
         callback was late, throttled, or never delivered. */
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        revealed.add(el);
        play();
        return;
      }

      observer.observe(el);
    });

    each((scope || document).querySelectorAll('[data-magnetic]'), function (el) {
      if (el.__wtMagnetic) return;
      el.__wtMagnetic = true;
      bindMagnetic(el);
    });
  }

  /* --------------------------------------------------- scroll-driven work */
  /* One passive listener, rAF-throttled, feeding parallax + header + progress.
     Everything else runs off IntersectionObserver. */

  var header = document.querySelector('[data-header]');
  var progress = document.querySelector('[data-read-progress]');
  var scrollPending = false;

  function readScroll() {
    scrollPending = false;
    var y = window.scrollY;

    if (header) header.classList.toggle('is-condensed', y > 80);

    if (progress) {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.setProperty('--progress', max > 0 ? y / max : 0);
    }

    if (panItems.length) {
      panItems.forEach(function (item) {
        if (!item.travel) return;
        var rect = item.el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        var progress = Math.min(1, Math.max(0, -rect.top / item.travel));
        item.el.style.setProperty('--pan', progress * item.travel);
      });
    }

    if (stackPanels.length) {
      var vh = window.innerHeight;
      stackPanels.forEach(function (panel, i) {
        var next = stackPanels[i + 1];
        if (!next) return;
        var nextTop = next.getBoundingClientRect().top;
        if (nextTop > vh || nextTop < -vh) return;
        /* 1 when the next panel is a full viewport away, 0 when it covers */
        var p = 1 - Math.min(1, Math.max(0, nextTop / vh));
        panel.style.transform = 'scale(' + (1 - p * 0.07) + ')';
        panel.style.opacity = 1 - p * 0.4;
      });
    }

    if (!isMinimal && parallaxItems.length) {
      var vh = window.innerHeight;
      parallaxItems.forEach(function (item) {
        var rect = item.el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var center = rect.top + rect.height / 2 - vh / 2;
        var travel = center * (1 - item.rate);
        item.el.style.setProperty('--py', Math.max(-30, Math.min(30, travel)) + 'px');
      });
    }
  }

  function onScroll() {
    if (scrollPending) return;
    scrollPending = true;
    requestAnimationFrame(readScroll);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    function () {
      measureChrome();
      panItems.forEach(function (item) {
        item.measure();
      });
      onScroll();
    },
    { passive: true }
  );

  /* --------------------------------------------------------------- marquee */

  function initMarquees(scope) {
    each((scope || document).querySelectorAll('[data-marquee]'), function (el) {
      if (el.__wtMarquee) return;
      el.__wtMarquee = true;
      var track = el.querySelector('.marquee__track');
      if (!track || track.children.length === 0) return;
      /* duplicate once so translateX(-50%) loops seamlessly */
      track.innerHTML += track.innerHTML;
      var speed = parseFloat(el.getAttribute('data-marquee-speed') || '42');
      el.style.setProperty('--marquee-duration', speed + 's');
      if (el.getAttribute('data-marquee') === 'reverse') {
        el.style.setProperty('--marquee-direction', 'reverse');
      }
    });
  }

  /* ---------------------------------------------------------- chrome height */
  /* A full-height hero starts below the announcement bar and the header, so
     100svh always overflows by exactly that much. Measure it rather than
     hard-coding: the announcement bar is a section the client can remove. */

  function measureChrome() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var top = main.getBoundingClientRect().top + window.scrollY;
    root.style.setProperty('--chrome-h', Math.max(0, Math.round(top)) + 'px');
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    measureChrome();
    scan(document);
    initMarquees(document);
    readScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
    initMarquees(e.target);
  });

  window.WTMotion = {
    tier: tier,
    scan: scan,
    define: define,
    onFrame: onFrame
  };
})();
